import React, { useEffect, useState, useRef } from "react";
import { api, API } from "../lib/api";
import { debugLog } from "../lib/debug";
import { Bell, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export const Notifications = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);

  const unread = items.filter(n => !n.read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      debugLog("notifications load failed:", e?.message);
    }
  };

  useEffect(() => {
    // Skip everything until the user is authenticated — prevents a noisy
    // "WS closed before connection established" warning during pre-login.
    if (!user) return;
    load();
    // Phase 12: cookie auth only. The browser sends the httpOnly cf_token
    // cookie on the same-origin WebSocket handshake automatically.
    const wsUrl = API.replace(/^http/, "ws") + "/ws/notifications";
    let ws;
    let interval;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "notification" && msg.data) {
            setItems(prev => [msg.data, ...prev]);
            toast.success(msg.data.title, { description: msg.data.body });
          }
        } catch (err) {
          debugLog("ws message parse failed:", err?.message);
        }
      };
      // keepalive ping
      interval = setInterval(() => {
        if (ws.readyState === 1) ws.send("ping");
      }, 30000);
    } catch (e) {
      debugLog("websocket setup failed:", e?.message);
    }
    return () => { if (ws) ws.close(); if (interval) clearInterval(interval); };
  }, [user?.id]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems(items.map(i => ({ ...i, read: true })));
  };

  return (
    <div className="relative" data-testid="notifications">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-md hover:bg-white/5" data-testid="notifications-bell">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF3B6B]" data-testid="notifications-unread-dot" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 glass-strong rounded-xl p-3 z-50 shadow-2xl" data-testid="notifications-panel">
          <div className="flex items-center justify-between p-2">
            <p className="font-semibold text-sm">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#FF3B6B] flex items-center gap-1" data-testid="mark-all-read-btn">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {items.length === 0 && <p className="text-center text-white/40 text-sm py-6">No notifications</p>}
            {items.map(n => (
              <div key={n.id} className={`p-3 rounded-lg ${n.read ? "" : "bg-white/5 border border-[#FF3B6B]/20"}`} data-testid={`notification-${n.id}`}>
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-white/60 mt-0.5">{n.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
